import { supabase } from '@/integrations/supabase/client';
import { Trek } from '@/lib/types/trek-types';
import { getImageUrl } from './utils';
import Analytics from '@/lib/analytics';
import { ItineraryItem, safelyParseItineraryItems } from '@/lib/utils';

/**
 * Get a specific trek by ID
 */
export async function getTrekById(id: string): Promise<Trek | null> {
  try {
    const { data, error } = await supabase
      .from('treks')
      .select('*')
      .eq('id', id)
      .maybeSingle();
      
    if (error) throw error;
    
    if (!data) {
      console.warn(`Trek with ID ${id} not found in database`);
      return null;
    }
    
    let images = data.images || [];
    
    if (!images.length) {
      const imageUrls: string[] = [];
      for (let i = 0; i < 5; i++) {
        try {
          const url = await getImageUrl(`treks/${id}_${i}.jpg`);
          imageUrls.push(url);
        } catch (error) {
          if (i > 0) continue;
          imageUrls.push(`https://images.unsplash.com/photo-${1500000000 + parseInt(id.substring(0, 6), 16) % 1000000}?w=800&auto=format&fit=crop`);
        }
      }
      
      if (imageUrls.length > 0) {
        images = imageUrls;
      }
    }
    
    const itineraryHighlights = safelyParseItineraryItems(data.itinerary_highlights);
    
    return {
      id: data.id,
      name: data.name,
      location: data.location,
      difficulty: data.difficulty as 'Easy' | 'Moderate' | 'Difficult' | 'Challenging',
      elevation: data.elevation,
      elevationUnit: (data.elevation_unit || 'feet') as 'feet' | 'meters',
      distance: data.distance,
      bestSeason: data.best_season,
      description: data.description,
      images: images,
      is_active: data.is_active,
      isTopTrek: data.is_top_trek || false,
      duration: data.duration || 0,
      itineraryHighlights,
    };
  } catch (error) {
    console.warn(`Error fetching trek ${id} from Supabase:`, error);
    return null;
  }
}

/**
 * Get all treks
 */
export async function getAllTreks(): Promise<Trek[]> {
  try {
    const { data, error } = await supabase
      .from('treks')
      .select('*')
      .eq('is_active', true)
      .order('priority', { ascending: false, nullsFirst: false })
      .order('name');
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return await Promise.all(data.map(async trek => {
        let images = trek.images || [];
        
        if (!images.length) {
          const imageUrls: string[] = [];
          for (let i = 0; i < 3; i++) {
            try {
              const url = await getImageUrl(`treks/${trek.id}_${i}.jpg`);
              imageUrls.push(url);
            } catch (error) {
              break;
            }
          }
          
          if (imageUrls.length > 0) {
            images = imageUrls;
          } else {
            images = [
              `https://images.unsplash.com/photo-${1500000000 + parseInt(trek.id.substring(0, 6), 16) % 1000000}?w=800&auto=format&fit=crop`
            ];
          }
        }
        
        const itineraryHighlights = safelyParseItineraryItems(trek.itinerary_highlights);
        
        return {
          id: trek.id,
          name: trek.name,
          location: trek.location,
          difficulty: trek.difficulty as 'Easy' | 'Moderate' | 'Difficult' | 'Challenging',
          elevation: trek.elevation,
          elevationUnit: (trek.elevation_unit || 'feet') as 'feet' | 'meters',
          distance: trek.distance,
          bestSeason: trek.best_season,
          description: trek.description,
          images: images,
          is_active: trek.is_active,
          isTopTrek: trek.is_top_trek || false,
          duration: trek.duration || 0,
          itineraryHighlights,
          priority: trek.priority
        };
      }));
    }
    
    return [];
  } catch (error) {
    console.warn('Error fetching treks from Supabase:', error);
    return [];
  }
}

/**
 * Get similar treks based on a trek ID
 */
export async function getSimilarTreks(trekId: string, limit = 3): Promise<Trek[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_simple_similar_treks', { 
        trek_id: trekId,
        limit_count: limit 
      });
      
    if (error) throw error;
    
    if (data && data.length > 0) {
      return data.map(trek => ({
        id: trek.id,
        name: trek.name,
        location: trek.location,
        difficulty: trek.difficulty as 'Easy' | 'Moderate' | 'Difficult' | 'Challenging',
        elevation: trek.elevation,
        elevationUnit: (trek.elevation_unit || 'feet') as 'feet' | 'meters',
        distance: trek.distance,
        bestSeason: trek.best_season,
        description: trek.description,
        images: trek.images || [],
        is_active: trek.is_active,
        isTopTrek: trek.is_top_trek || false,
        duration: trek.duration || 0,
        itineraryHighlights: safelyParseItineraryItems(trek.itinerary_highlights),
      }));
    }
    
    return [];
  } catch (error) {
    console.error(`Error fetching similar treks to ${trekId}:`, error);
    return [];
  }
}

/**
 * Search treks based on query
 */
export async function searchTreks(query: string): Promise<Trek[]> {
  if (!query || query.trim().length === 0) {
    return [];
  }
  
  console.log('Searching for:', query);

  Analytics.track('Trek Search', {
    query: query,
    source: 'search_service'
  });

  try {
    const searchTerm = query.trim().toLowerCase();
    
    const nearMatch = searchTerm.match(/\b(near|around|close to|nearby)\s+([a-z\s]+)/i);
    
    if (nearMatch && nearMatch[2]) {
      const locationName = nearMatch[2].trim();
      console.log('Location-based search detected for:', locationName);
      
      Analytics.track('Location Search', {
        query: query,
        location: locationName
      });
      
      // Use type assertion to handle TypeScript errors with new database function
      const { data, error } = await supabase
        .rpc('search_treks_near_location', { location_query: locationName }) as {
          data: any[] | null;
          error: any;
        };
        
      if (error) {
        console.error('Error in location search:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        console.log(`No results found near ${locationName}`);
        return [];
      }
      
      console.log(`Found ${data.length} results near ${locationName}`);
      
      return data.map(trek => {
        const itineraryHighlights = safelyParseItineraryItems(trek.itinerary_highlights);
        
        return {
          id: trek.id,
          name: trek.name,
          location: trek.location,
          difficulty: trek.difficulty as 'Easy' | 'Moderate' | 'Difficult' | 'Challenging',
          elevation: trek.elevation,
          elevationUnit: (trek.elevation_unit || 'feet') as 'feet' | 'meters',
          distance: trek.distance,
          bestSeason: trek.best_season,
          description: trek.description,
          images: trek.images || [],
          is_active: trek.is_active,
          isTopTrek: trek.is_top_trek || false,
          duration: trek.duration || 0,
          itineraryHighlights,
          rank: trek.rank
        };
      });
    }
    
    // Use type assertion to handle TypeScript errors with new database function
    const { data, error } = await supabase
      .rpc('search_treks', { search_query: searchTerm }) as {
        data: any[] | null;
        error: any;
      };
      
    if (error) {
      console.error('Error in full-text search:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      console.log('No results found with full-text search');
      return [];
    }
    
    console.log(`Found ${data.length} results with full-text search`);
    
    Analytics.track('Search Results', {
      query: query,
      results_count: data.length || 0,
      search_type: 'full_text'
    });
    
    return data.map(trek => {
      const itineraryHighlights = safelyParseItineraryItems(trek.itinerary_highlights);
        
      return {
        id: trek.id,
        name: trek.name,
        location: trek.location,
        difficulty: trek.difficulty as 'Easy' | 'Moderate' | 'Difficult' | 'Challenging',
        elevation: trek.elevation,
        elevationUnit: (trek.elevation_unit || 'feet') as 'feet' | 'meters',
        distance: trek.distance,
        bestSeason: trek.best_season,
        description: trek.description,
        images: trek.images || [],
        is_active: trek.is_active,
        isTopTrek: trek.is_top_trek || false,
        duration: trek.duration || 0,
        itineraryHighlights,
        rank: trek.rank
      };
    });
  } catch (error) {
    console.error('Supabase search failed, falling back to client-side search:', error);
    
    Analytics.track('Search Error', {
      query: query,
      error: String(error).substring(0, 100)
    });
    
    const allTreks = await getAllTreks();
    const searchTerm = query.trim().toLowerCase();
    
    const searchWords = searchTerm.split(/\s+/).filter(word => word.length > 1);
    
    const results = allTreks.filter(trek => {
      if (searchWords.length <= 1) {
        return trek.name.toLowerCase().includes(searchTerm) ||
          trek.location.toLowerCase().includes(searchTerm) ||
          trek.difficulty.toLowerCase().includes(searchTerm) ||
          trek.description.toLowerCase().includes(searchTerm) ||
          trek.bestSeason.toLowerCase().includes(searchTerm);
      }
      
      let matchCount = 0;
      const trekString = `${trek.name} ${trek.location} ${trek.difficulty} ${trek.description} ${trek.bestSeason}`.toLowerCase();
      
      searchWords.forEach(word => {
        if (trekString.includes(word)) matchCount++;
      });
      
      return matchCount > 0;
    });
    
    if (searchWords.length > 1) {
      results.sort((a, b) => {
        const aMatches = searchWords.filter(word => 
          a.name.toLowerCase().includes(word) || 
          a.location.toLowerCase().includes(word) ||
          a.description.toLowerCase().includes(word)
        ).length;
        
        const bMatches = searchWords.filter(word => 
          b.name.toLowerCase().includes(word) || 
          b.location.toLowerCase().includes(word) ||
          b.description.toLowerCase().includes(word)
        ).length;
        
        return bMatches - aMatches;
      });
    }
    
    Analytics.track('Fallback Search Used', {
      query: query,
      results_count: results.length
    });
    
    console.log('Search results from client-side fallback:', results.length);
    return results;
  }
}
